import { type BackgroundColorToken } from '@canvas/constants/colors/tokens-types';

import { type Participant } from './types.ts';
import { type FieldEntryGrouping } from './types.ts';
import { getKey, KEY_DELIMITER } from './utils.ts';

/**
 * The participants present on one grouping, reduced to what the drawable
 * presence layer needs to render. `fieldId` / `entryId` are `null` when the
 * grouping targets an entire column / row respectively.
 */
export interface PresenceLayerGroupSnapshot {
  fieldId: string | null;
  entryId: string | null;
  participants: Array<{
    colorToken: BackgroundColorToken;
    displayName: string | undefined;
  }>;
}

function denormalize(id: string): string | null {
  return id === 'null' ? null : id;
}

/**
 * A dedicated, self-contained presence store for the drawable presence layer.
 *
 * It exposes the same `addParticipant` / `removeParticipant` surface the shared
 * `PresenceState` does, so it can be used as the telepointer sink in its place.
 * Unlike the shared store it does not notify individual React components via the
 * ping registry; instead it exposes a single `subscribe` that the selections
 * presence effect uses to redraw its highlights layer whenever presence changes.
 *
 * When this store is used as the sink, the shared `PresenceState` singleton
 * receives nothing, so the legacy per-component presence path renders nothing
 * without any edits to those components.
 */
export class PresenceLayerState {
  #participants: Map<string, Participant[]> = new Map();
  #groupingKeyByAccountId: Map<string, string> = new Map();
  #listeners = new Set<() => void>();

  addParticipant(grouping: FieldEntryGrouping, participant: Participant): void {
    this.removeParticipant(participant, false);

    const key = getKey(grouping);
    const existing = this.#participants.get(key);

    if (existing) {
      existing.unshift(participant);
    } else {
      this.#participants.set(key, [participant]);
    }

    this.#groupingKeyByAccountId.set(participant.identity.accountId, key);
    this.#notify();
  }

  removeParticipant(participant: Participant, notify = true): void {
    const key = this.#groupingKeyByAccountId.get(participant.identity.accountId);

    if (!key) {
      return;
    }

    const remaining = this.#participants
      .get(key)
      ?.filter((p) => p.identity.accountId !== participant.identity.accountId);

    if (remaining && remaining.length > 0) {
      this.#participants.set(key, remaining);
    } else {
      this.#participants.delete(key);
    }

    this.#groupingKeyByAccountId.delete(participant.identity.accountId);

    if (notify) {
      this.#notify();
    }
  }

  get accountIdToGroupingKeyMap(): ReadonlyMap<string, string> {
    return this.#groupingKeyByAccountId;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);

    return () => {
      this.#listeners.delete(listener);
    };
  }

  getGroups(): PresenceLayerGroupSnapshot[] {
    const groups: PresenceLayerGroupSnapshot[] = [];

    for (const [key, participants] of this.#participants) {
      const [fieldId, entryId] = key.split(KEY_DELIMITER);

      groups.push({
        fieldId: denormalize(fieldId),
        entryId: denormalize(entryId),
        participants: participants.map((participant) => ({
          colorToken: participant.colorToken,
          displayName: participant.identity.displayName
        }))
      });
    }

    return groups;
  }

  #notify(): void {
    for (const listener of this.#listeners) {
      listener();
    }
  }
}
